import React, { useEffect, useState } from 'react';
import { Modal, Button, Header, Loader } from 'semantic-ui-react';
import { Bar, Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';
import 'chart.js';
import {
  getAnalysisEvolution,
  getClientActivity,
  getPeriodActivity,
  AnalysisEvolutionData,
  ClientActivityData,
  PeriodActivityData
} from '../../../services/dashboard';

// Estilos para a Modal
const StyledModal = styled(Modal)`
  max-width: 900px !important;
  width: 900px !important;
`;

const ChartContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 20px;
`;

const ChartBox = styled.div`
  width: 30%;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 5px;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 10px;
`;

const PeriodSelector = styled.div`
  text-align: center;
  margin-bottom: 10px;
  font-size: 12px;
  color: #666;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 250px;
`;

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  productType: string;
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  onExport?: () => void;
}

const SummaryModal: React.FC<SummaryModalProps> = ({
  isOpen,
  onClose,
  title,
  productType,
  estateId,
  franchiseId,
  managerId,
  onExport
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [analysisEvolution, setAnalysisEvolution] = useState<AnalysisEvolutionData>({
    labels: [],
    data: []
  });
  const [clientActivity, setClientActivity] = useState<ClientActivityData>({
    labels: [],
    data: []
  });
  const [periodActivity, setPeriodActivity] = useState<PeriodActivityData>({
    labels: [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez'
    ],
    data: []
  });

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, productType, estateId, franchiseId, managerId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];

      const params = {
        estateId,
        franchiseId,
        managerId,
        startDate,
        endDate
      };

      const [evolutionData, activityData, periodData] = await Promise.all([
        getAnalysisEvolution(productType, params),
        getClientActivity(productType, params),
        getPeriodActivity(productType, params)
      ]);

      setAnalysisEvolution(evolutionData);
      setClientActivity(activityData);
      setPeriodActivity(periodData);
    } catch (error) {
      console.error('Erro ao carregar dados para o modal de resumo:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const evolutionChartData = {
    labels: analysisEvolution.labels,
    datasets: [
      {
        label: 'Quantidade',
        data: analysisEvolution.data,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  };

  const activityChartData = {
    labels: clientActivity.labels,
    datasets: [
      {
        data: clientActivity.data,
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(255, 205, 86, 0.6)',
          'rgba(54, 162, 235, 0.6)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  const periodChartData = {
    labels: periodActivity.labels,
    datasets: [
      {
        label: 'Quantidade',
        data: periodActivity.data,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  const enhancedBarOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            precision: 0
          }
        }
      ]
    },
    legend: {
      display: false
    },
    tooltips: {
      enabled: true
    }
  };

  const enhancedDoughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 60,
    legend: {
      display: true,
      position: 'right',
      labels: {
        fontSize: 12,
        padding: 10,
        boxWidth: 15
      }
    },
    tooltips: {
      enabled: true
    }
  };

  return (
    <StyledModal open={isOpen} onClose={onClose} closeIcon>
      <Header content={title} />
      <Modal.Content scrolling>
        <ChartContainer>
          <ChartBox>
            <ChartTitle>Evolução das análises</ChartTitle>
            <PeriodSelector>
              <span>Últimos 30 dias</span>
            </PeriodSelector>
            <div style={{ height: '250px', position: 'relative' }}>
              {isLoading ? (
                <LoaderContainer>
                  <Loader active inline="centered" />
                </LoaderContainer>
              ) : (
                <Bar data={evolutionChartData} options={enhancedBarOptions} />
              )}
            </div>
          </ChartBox>

          <ChartBox>
            <ChartTitle>Atividade do cliente</ChartTitle>
            <PeriodSelector>
              <span>Últimos 30 dias</span>
            </PeriodSelector>
            <div style={{ height: '250px', position: 'relative', margin: '0 auto' }}>
              {isLoading ? (
                <LoaderContainer>
                  <Loader active inline="centered" />
                </LoaderContainer>
              ) : (
                <Doughnut data={activityChartData} options={enhancedDoughnutOptions} />
              )}
            </div>
          </ChartBox>

          <ChartBox>
            <ChartTitle>Atividade no período</ChartTitle>
            <PeriodSelector>
              <span>Últimos 12 meses</span>
            </PeriodSelector>
            <div style={{ height: '250px', position: 'relative' }}>
              {isLoading ? (
                <LoaderContainer>
                  <Loader active inline="centered" />
                </LoaderContainer>
              ) : (
                <Bar data={periodChartData} options={enhancedBarOptions} />
              )}
            </div>
          </ChartBox>
        </ChartContainer>
      </Modal.Content>
      <Modal.Actions>
        <ButtonContainer>
          <Button
            style={{ backgroundColor: '#59249b', color: '#fff' }}
            icon="download"
            content="Exportar relatório"
            labelPosition="left"
            onClick={onExport}
            disabled={isLoading}
          />
          <Button onClick={onClose}>Fechar</Button>
        </ButtonContainer>
      </Modal.Actions>
    </StyledModal>
  );
};

export default SummaryModal;
